/**
 * **Buka kembali bill LUNAS** — dan sampai layar ini ada, kebijakannya menjaga pintu yang tak
 * bisa dibuka siapa pun.
 *
 * ### ⭐ Kasusnya bukan koreksi akuntansi
 * Kasir memilih QRIS padahal tamu membayar tunai, atau lupa menerapkan diskon — dan **tamunya
 * masih berdiri di depan kasir**. Memaksanya jadi refund berarti menerbitkan dua peristiwa
 * ekonomi palsu (bayar lalu kembalikan) untuk satu kejadian yang tak pernah terjadi.
 *
 * ### ⛔⛔ Layar TIDAK menebak boleh-tidaknya
 * Ada empat gerbang, dan semuanya di server: izin `order:reopen` · sesi kas masih terbuka ·
 * belum di-`acked` ERP · **kebijakan tunai per-outlet** (`allowReopenAfterCashTender`, bawaannya
 * MENOLAK). Klien yang ikut menebak akan salah menebak — setelan outlet berubah tanpa layar ini
 * tahu, dan kasir melihat pintu yang berbeda dari pintu yang sebenarnya dijaga.
 *
 * ⇒ Tombolnya selalu ada, penolakannya ditampilkan apa adanya. `ORDER-409-REOPEN_DENIED` sudah
 * membawa kalimat yang menyebut sebab DAN jalan keluarnya (refund) — menerjemahkannya ulang di
 * sini hanya membuat dua versi kebenaran yang bisa berselisih.
 *
 * ### ⚠️ Kenapa peringatannya dibaca SEBELUM menekan
 * Yang paling mahal bukan penolakan, melainkan kasir yang berhasil membuka lalu tak menutupnya
 * kembali: bill itu berdiri sebagai `billed` dengan seluruh tender terbalik, dan sesi kas ditutup
 * dengan uang yang tak punya penjualan. Kalimat peringatannya menyebut itu.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';

/**
 * ⛔ Enum kontrak. Ketiganya adalah sebab NYATA yang disebut JSDoc `reopenOrder`:
 * salah metode bayar · diskon terlewat · sisanya wajib berketerangan.
 */
const REASONS = ['wrong_tender', 'missed_discount', 'other'] as const;

export interface ReopenButtonProps {
  /** Bill lunas yang baru saja ditutup di terminal ini. */
  readonly orderId: string;
  /** Dipanggil sesudah berhasil — pemanggil memuat ulang billnya untuk disunting. */
  readonly onReopened: (orderId: string) => void | Promise<void>;
}

export function ReopenButton({ orderId, onReopened }: ReopenButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<(typeof REASONS)[number]>('wrong_tender');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⛔ `other` tanpa penjelasan = teks bebas jilid dua. Layar menolak lebih dulu; server tetap jaring.
  const needsNote = reason === 'other' && note.trim() === '';

  const close = () => {
    setOpen(false);
    setNote('');
    setError(null);
  };

  return (
    <>
      <button
        type="button"
        className="action-secondary"
        data-testid="reopen-button"
        onClick={() => setOpen(true)}
      >
        {t('cashier.reopen.button')}
      </button>

      {open && (
        <div
          className="dialog"
          role="dialog"
          aria-label={t('cashier.reopen.title')}
          data-testid="reopen-dialog"
        >
          <h2>{t('cashier.reopen.title')}</h2>

          {/* ⚠️ Batasnya dibaca SEBELUM menekan — yang ditolak sesudahnya akan mengira sistem rusak. */}
          <p className="dialog-note">{t('cashier.reopen.warning')}</p>

          <label>
            {t('cashier.reopen.reason')}
            <select
              data-testid="reopen-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as (typeof REASONS)[number])}
            >
              {REASONS.map((a) => (
                <option key={a} value={a}>{t(`enum.reasonCode.${a}`)}</option>
              ))}
            </select>
          </label>

          {reason === 'other' && (
            <label>
              {t('cashier.reopen.note')}
              <input data-testid="reopen-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          )}

          {/* ⭐ Pesan server ditampilkan APA ADANYA — ia sudah menyebut sebab dan jalan keluarnya. */}
          {error !== null && <p className="error" data-testid="reopen-error">{error}</p>}

          <div className="dialog-actions">
            <button type="button" data-testid="reopen-cancel" onClick={close}>
              {t('cashier.reopen.cancel')}
            </button>
            <button
              type="button"
              className="action-primary"
              data-testid="reopen-confirm"
              disabled={needsNote || busy}
              onClick={() => {
                setBusy(true);
                setError(null);
                void api
                  .reopen(orderId, {
                    reasonCode: reason,
                    ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
                  })
                  .then(async () => {
                    close();
                    await onReopened(orderId);
                  })
                  .catch((e: Error) => { setError(e.message); })
                  .finally(() => { setBusy(false); });
              }}
            >
              {t('cashier.reopen.confirm')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
